import React from "react";
import classNames from "classnames";
import {useEffect, useState} from "react";
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import {idExpression, nameExpression, DialogType} from "../util"
import {ISystem, System} from "../models/system";
import Alert from "react-bootstrap/Alert";

export interface ModalEditSystemProps {
    show: boolean,
    title: string,
    type: DialogType,
    handleSave: (sys:ISystem) => void,
    handleClose: () => void,
    systemValues: ISystem
}

export const ModalEditSystem: React.FC<ModalEditSystemProps> = (props: ModalEditSystemProps) => {
    const [systemId, setSystemId] = useState("");
    const [idValid, setIdValid] = useState(true);
    const [systemName, setSystemName] = useState("");
    const [nameValid, setNameValid] = useState(true);
    const [description, setDescription] = useState("");

    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    function displayAlert(message:string) {
        setAlertMessage(message);
        setShowAlert(true);
    }

    function clearAlert() {
        setAlertMessage("");
        setShowAlert(false);
    }

    useEffect(() => {
        // Copy values sent to this component from the parent to the form fields.
        setSystemId(props.systemValues.id);
        setIdValid(true);
        setSystemName(props.systemValues.name);
        setNameValid(true);
        setDescription(props.systemValues.desc);
        clearAlert();
    }, [props.systemValues]);

    const updateId = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSystemId(event.target.value)
        setIdValid(event.target.value.length === 0 || idExpression.test(event.target.value))
    }

    const updateName = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSystemName(event.target.value)
        setNameValid(event.target.value.length === 0 || nameExpression.test(event.target.value))
    }

    const handleSave = () => {
        // We need to run regex tests here because idValid and nameValid are set only if the user edits the fields.
        if (!idExpression.test(systemId) || !nameExpression.test(systemName)) {
            displayAlert("Enter valid system ID and name");
        }
        else {
            var sys = new System(systemId, systemName, description);

            setSystemId("");
            setSystemName("");
            setDescription("");
            clearAlert();

            // Return new system to the parent.
            props.handleSave(sys);
        }
    }

    const handleClose = () => {
        // Note we are not returning anything to the parent.
        setSystemId("");
        setSystemName("");
        setDescription("");
        clearAlert();
        props.handleClose();
    }

    return (
        <Modal show={props.show} backdrop="static" scrollable={true}>
            <Modal.Header>
                <Modal.Title>{props.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Label>System ID</Form.Label>
                    <Form.Group className={classNames("mb-3")} controlId="system-id">

                        {
                        props.type === DialogType.ADD &&
                        <Form.Control type="text" value={systemId} isInvalid={!idValid} onChange={(event) => updateId(event)}/>
                        }

                        {
                        props.type === DialogType.EDIT &&
                        <Form.Control type="text" value={systemId} disabled/>
                        }

                        <Form.Text className="text-muted">
                            System ID must be unique for this site. It should be short and contain only
                            lowercase letters, digits, underscores, and hyphens.
                            An existing ID cannot be changed.
                        </Form.Text>
                    </Form.Group>
                    <Form.Label>System Name</Form.Label>
                    <Form.Group className={classNames("mb-3")} controlId="system-name">
                        <Form.Control type="text" value={systemName} isInvalid={!nameValid} onChange={(event) => updateName(event)}/>
                        <Form.Text className="text-muted">
                            Enter a friendly name for the system.
                            It can contain uppercase and lowercase letters, digits, underscores, hyphens, periods and spaces.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className={classNames("mb-3")} controlId="desc">
                        <Form.Label>Description</Form.Label>
                        <Form.Control type="text" value={description}  placeholder="(optional)" onChange={(event) => setDescription(event.target.value)}/>
                    </Form.Group>

                    {/* This alert is for showing errors. It is always visible. */}
                    <Alert variant={showAlert ? "danger" : "light"}>
                        {alertMessage}
                    </Alert>
               </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                <Button variant="primary" onClick={handleSave}>Save</Button>
            </Modal.Footer>
        </Modal>
    );
}